import "@/App.css";
import { BrowserRouter, Route, Routes, useLocation, Outlet } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { StoreProvider } from "@/context/StoreContext";
import { Header, Footer } from "@/components/site/Layout";
import { RouteSeo } from "@/components/site/Seo";
import Home from "@/pages/store/Home";
import Shop from "@/pages/store/Shop";
import { Cart, Checkout, PaymentResult } from "@/pages/store/Checkout";
import { Login, Register, ForgotPassword, ResetPassword, VerifyEmail, AuthCallback } from "@/pages/store/Auth";
import Account, { OrderDetail } from "@/pages/store/Account";
import { ContentPage, Journey, Faq, Contact } from "@/pages/store/Content";
import { WaitlistConfirm, Unsubscribe } from "@/pages/store/LaunchList";
import { AdminShell, AdminLogin, AdminSetup } from "@/admin/AdminApp";
import { Overview, Orders, OrderView } from "@/admin/Orders";
import { Inventory, Products } from "@/admin/Inventory";
import { Customers, CustomerView, Credits, Marketing, Enquiries } from "@/admin/People";
import { Reports, Content, SettingsPage, Launch } from "@/admin/Config";

const Store = () => (
  <div className="min-h-screen flex flex-col">
    <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:p-3 focus:bg-white z-[60]">Skip to content</a>
    <Header />
    <div id="main" className="flex-1"><Outlet /></div>
    <Footer />
  </div>
);

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <>
    <RouteSeo />
    <Routes>
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin/setup" element={<AdminSetup />} />
      <Route path="/admin" element={<AdminShell />}>
        <Route index element={<Overview />} />
        <Route path="orders" element={<Orders />} />
        <Route path="orders/:id" element={<OrderView />} />
        <Route path="fulfilment" element={<Orders fulfilment />} />
        <Route path="inventory" element={<Inventory />} />
        <Route path="products" element={<Products />} />
        <Route path="customers" element={<Customers />} />
        <Route path="customers/:id" element={<CustomerView />} />
        <Route path="credits" element={<Credits />} />
        <Route path="marketing" element={<Marketing />} />
        <Route path="reports" element={<Reports />} />
        <Route path="content" element={<Content />} />
        <Route path="enquiries" element={<Enquiries />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="launch" element={<Launch />} />
      </Route>
      <Route element={<Store />}>
        <Route path="/" element={<Home />} />
        <Route path="/shop" element={<Shop />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/payment/success" element={<PaymentResult />} />
        <Route path="/payment/cancel" element={<PaymentResult cancelled />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/account" element={<Account />} />
        <Route path="/account/orders/:id" element={<OrderDetail />} />
        <Route path="/about" element={<ContentPage type="page" slug="about" />} />
        <Route path="/journey" element={<Journey />} />
        <Route path="/journey/:slug" element={<ContentPage type="journey" />} />
        <Route path="/faq" element={<Faq />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/policies/:slug" element={<ContentPage type="policy" />} />
        <Route path="/launch-list/confirm" element={<WaitlistConfirm />} />
        <Route path="/unsubscribe" element={<Unsubscribe />} />
        <Route path="*" element={<main className="max-w-3xl mx-auto px-5 py-24"><h1 className="text-4xl">Page not found</h1></main>} />
      </Route>
    </Routes>
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <StoreProvider>
        <AppRouter />
        <Toaster position="top-center" />
      </StoreProvider>
    </BrowserRouter>
  );
}
